/**
 * Pure helper functions for plan change modal logic.
 * No JSX — safe for vitest without JSX transform issues.
 */

/**
 * Determine whether a plan change is an upgrade, downgrade, or lateral switch.
 * @param {Object} currentPlan
 * @param {Object} targetPlan
 * @returns {{ verb: string, gerund: string }}
 */
export function getChangeVerb(currentPlan, targetPlan) {
  if (targetPlan.allotment > currentPlan.allotment) {
    return { verb: "Upgrade", gerund: "Upgrading" };
  }
  if (targetPlan.allotment < currentPlan.allotment) {
    return { verb: "Downgrade", gerund: "Downgrading" };
  }

  // Allotments are equal — compare effective monthly price
  const currentMonthly = currentPlan.monthlyPrice ?? currentPlan.price;
  const targetMonthly = targetPlan.monthlyPrice ?? targetPlan.price;

  if (targetMonthly > currentMonthly) {
    return { verb: "Upgrade", gerund: "Upgrading" };
  }
  if (targetMonthly < currentMonthly) {
    return { verb: "Downgrade", gerund: "Downgrading" };
  }

  return { verb: "Switch", gerund: "Switching" };
}

/**
 * Format a number with commas (e.g. 1000000 → "1,000,000").
 * Matches Utils.numberWithCommas behavior.
 * @param {number} x
 * @returns {string}
 */
export function formatCredits(x) {
  if (x === null || x === undefined) return "";
  if (x === 0) return "0";
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Format a price in cents to a dollar string (e.g. 2900 → "$29").
 * @param {number} priceInCents
 * @returns {string}
 */
export function formatPrice(priceInCents) {
  return `$${priceInCents / 100}`;
}

/**
 * Format the effective monthly price of a plan.
 * Uses monthlyPrice if available, falls back to price.
 * @param {Object} plan
 * @returns {string} e.g. "$29/mo"
 */
export function formatMonthlyPrice(plan) {
  const monthlyPrice = plan.monthlyPrice ?? plan.price;
  return `$${monthlyPrice / 100}/mo`;
}

/**
 * Determine rollover gain/loss when switching plans.
 * @param {Object} currentPlan
 * @param {Object} targetPlan
 * @returns {{ losingRollover: boolean, gainingRollover: boolean }}
 */
export function getRolloverInfo(currentPlan, targetPlan) {
  return {
    losingRollover: !!currentPlan.rollover && !targetPlan.rollover,
    gainingRollover: !currentPlan.rollover && !!targetPlan.rollover,
  };
}
